import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConstantService } from 'src/app/_services/constant.service';

/**
 * Operations Drill-down — full-page version.
 *
 * Why this exists: the inline panel + Material Dialog versions both hit a
 * fundamental Angular change-detection issue in this codebase where bindings
 * (*ngIf / [class] / interpolations) don't re-evaluate after the initial mount.
 * That makes any "loading → data" transition fail silently.
 *
 * This page sidesteps the entire problem by being a fresh route. The parent
 * dashboard fetches the rows BEFORE navigating, then passes them via
 * router.navigate(..., { state: {...} }). This component reads that state in
 * its constructor and renders the data table on its very first render — no
 * post-mount state transitions, ever.
 *
 * If state is missing (e.g. user reloaded the page or pasted the URL), we
 * gracefully show "No data — return to the dashboard" rather than refetching,
 * since refetching would re-introduce the transition issue.
 */
@Component({
    selector: 'app-operations-drilldown-page',
    templateUrl: './operations-drilldown-page.component.html',
    styleUrls: ['./operations-drilldown-page.component.scss'],
    standalone: false,
})
export class OperationsDrilldownPageComponent implements OnInit {

    /** All fields are set ONCE in the constructor from router state. Never mutated. */
    readonly metric: string;
    readonly title: string;
    readonly rows: any[];
    readonly error: string | null;
    readonly locationId: number | null;
    readonly startDate: Date | null;
    readonly endDate: Date | null;
    readonly missingState: boolean;

    /** RoomStatusEnum code → readable name. */
    private readonly ROOM_STATUS_NAMES: { [code: number]: string } = {
        1: 'Occupied', 2: 'Dirty', 3: 'Housekeeping',
        4: 'Ready for Inspection', 5: 'Inspection', 6: 'Clean / Ready',
        7: 'Guest Checked-In', 8: 'In Progress', 9: 'Not Available',
    };
    /** Reservation status code → readable name. */
    private readonly RES_STATUS_NAMES: { [code: string]: string } = {
        'QO': 'Quote Open', 'QH': 'Quote Hold', 'QE': 'Quote Expired',
        'OP': 'Reserved', 'OC': 'Sale Closed', 'PP': 'Sale Postponed',
        'OX': 'Cancelled', 'NA': 'Maintenance',
        'OO': 'Owner Occupied', 'OG': 'Owner Guest Occupied',
    };
    /** Reservation status code → background colour. Mirrors
     *  ConstantService.reservationStatuses so cells on this page look
     *  identical to the canonical /reservations list. */
    private readonly RES_STATUS_COLORS: { [code: string]: string } = {
        'QO': 'lightgreen', 'QH': 'yellow',    'QE': 'purple',
        'OP': 'lightgrey',  'OC': 'green',     'PP': 'lightblue',
        'OX': 'darkred',    'NA': 'darkgrey',
        'OO': 'hotpink',    'OG': 'lightpink',
    };

    /** Background colour for a reservation-status cell; '' for unknown codes
     *  so the binding produces no inline style (the cell uses default styling). */
    resStatusColor(code: string | null | undefined): string {
        if (code == null) return '';
        return this.RES_STATUS_COLORS[String(code)] || '';
    }

    constructor(private router: Router, private route: ActivatedRoute,
                public constantService: ConstantService) {
        // Drill-down payload is handed off via localStorage with a one-shot
        // unique key in the query string. history.state doesn't cross tab
        // boundaries; localStorage does. Read + delete to keep things tidy.
        let state: any = {};
        const key = this.route.snapshot.queryParamMap.get('key');
        if (key) {
            try {
                const raw = localStorage.getItem(key);
                if (raw) {
                    state = JSON.parse(raw);
                    localStorage.removeItem(key);
                }
            } catch (e) {
                // Fall through to missingState path below.
            }
        }
        this.metric     = state.metric || '';
        this.title      = state.title || 'Drill-down';
        this.rows       = Array.isArray(state.rows) ? state.rows : [];
        this.error      = state.error || null;
        this.locationId = state.locationId ?? null;
        this.startDate  = state.startDate ? new Date(state.startDate) : null;
        this.endDate    = state.endDate   ? new Date(state.endDate)   : null;
        // If we got here without payload (user pasted URL, refreshed the tab
        // after closing/reopening, etc.), show a clear message.
        this.missingState = !state.metric;
    }

    ngOnInit(): void { /* no-op — all setup in constructor */ }

    /**
     * Close this drill-down tab. Since the drill-down is opened with
     * window.open(..., '_blank'), the user's dashboard is still in the
     * original tab — closing returns them there without losing context.
     * If for some reason window.close() can't run (page wasn't opened by
     * script, etc.), fall back to navigating back to the dashboard.
     */
    back(): void {
        try {
            window.close();
        } catch (e) { /* swallow */ }
        // window.close() silently fails when the tab wasn't opened by script
        // (e.g. user pasted the URL into a new tab). Navigate as a fallback.
        this.router.navigate(['/operations-dashboard']);
    }

    /** Build /property-edit/<id> URL. */
    propertyEditUrl(strId: any): string {
        if (strId == null || strId === '') return 'javascript:void(0)';
        return this.router.serializeUrl(this.router.createUrlTree(['/property-edit', strId]));
    }

    /** Build /order/<id> URL. */
    orderUrl(orderId: any): string {
        if (orderId == null || orderId === '') return 'javascript:void(0)';
        return this.router.serializeUrl(this.router.createUrlTree(['/order', orderId]));
    }

    /**
     * Column configuration for the current metric.
     *
     * Column types:
     *   - 'date' / 'num' / 'money'       — value formatting only
     *   - 'resStatus' / 'roomStatus'     — code → readable name
     *   - 'propertyLink'                 — value IS the STR id; renders as link
     *   - 'propertyCodeLink' + linkField — displays propertyCode but links
     *                                       to /property-edit/{row[linkField]}
     *   - 'reservationLink' + linkField  — displays reservationId but links
     *                                       to /order/{row[linkField]}
     */
    columns(): { field: string; header: string; type?: string; linkField?: string }[] {
        switch (this.metric) {
            case 'arrivals':
            case 'departures':
                return [
                    { field: 'reservationId',      header: 'Res ID',    type: 'reservationLink', linkField: 'orderId' },
                    { field: 'propertyCode',       header: 'Property',  type: 'propertyCodeLink', linkField: 'shortTermRentalId' },
                    { field: 'guestFirstName',     header: 'First' },
                    { field: 'guestLastName',      header: 'Last' },
                    // KBM policy: 4:00 PM check-in / 11:00 AM check-out. The
                    // underlying date-only columns store midnight UTC, so a
                    // plain {timeStyle:'short'} render shows the browser's
                    // tz-shifted midnight instead of the actual policy time.
                    { field: 'checkInDateTime',    header: 'Check-in',  type: 'checkInPolicy' },
                    { field: 'checkOutDateTime',   header: 'Check-out', type: 'checkOutPolicy' },
                    // Arrival-Status / Departure-Status tag names (e.g. "Early
                    // Check-In 10AM", "Late Checkout 2PM"). NULL → '—' which
                    // means the guest is on the standard 4PM in / 11AM out.
                    { field: 'earlyCheckInTag',    header: 'Early Check-in' },
                    { field: 'lateCheckoutTag',    header: 'Late Checkout' },
                    { field: 'numberOfNightsStay', header: 'Nights',    type: 'num'  },
                    { field: 'reservationStatus',  header: 'Status',    type: 'resStatus' },
                ];
            case 'occupied':
                // Rooms Occupied drill-down — per manager request:
                //   • Name column removed (propertyCode is the actionable handle).
                //   • Bathrooms column added next to Bedrooms.
                //   • Status cell linked to the order of the occupying
                //     reservation via the new 'roomStatusLink' cell type
                //     (orderId comes down as occupyingOrderId, picked by the
                //     backend's MIN(r.orderId) per unit).
                return [
                    { field: 'shortTermRentalId', header: 'STR ID',    type: 'propertyLink' },
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'bedrooms',          header: 'Bedrooms',  type: 'num' },
                    { field: 'bathrooms',         header: 'Bathrooms', type: 'num' },
                    { field: 'occupancyStatus',   header: 'Status',    type: 'roomStatusLink', linkField: 'occupyingOrderId' },
                ];
            case 'vacantClean':
            case 'outOfOrder':
            case 'cleaningBacklog':
            case 'allUnits':
            case 'unitsDirty':
            case 'unitsHousekeeping':
            case 'unitsInspection':
                return [
                    { field: 'shortTermRentalId', header: 'STR ID',   type: 'propertyLink' },
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'propertyName',      header: 'Name' },
                    { field: 'bedrooms',          header: 'Bedrooms', type: 'num' },
                    { field: 'occupancyStatus',   header: 'Status',   type: 'roomStatus' },
                ];
            case 'futonsEligible':
            case 'rollawaysEligible':
                return [
                    { field: 'shortTermRentalId', header: 'STR ID',   type: 'propertyLink' },
                    { field: 'propertyCode',      header: 'Property' },
                    { field: 'propertyName',      header: 'Name' },
                    { field: 'bedrooms',          header: 'Bedrooms', type: 'num' },
                    { field: 'capacity',          header: 'Capacity', type: 'num' },
                ];
            case 'futonsRevenue':
            case 'rollawaysRevenue':
                return [
                    { field: 'orderId',     header: 'Order',      type: 'orderLink' },
                    { field: 'saleDate',    header: 'Sale Date',  type: 'date' },
                    { field: 'propertyCode',header: 'Property',   type: 'propertyCodeLink', linkField: 'shortTermRentalId' },
                    { field: 'productName', header: 'Product' },
                    { field: 'quantity',    header: 'Qty',        type: 'num' },
                    { field: 'listPrice',   header: 'List Price', type: 'money' },
                    { field: 'adjustment',  header: 'Adj',        type: 'money' },
                    { field: 'lineTotal',   header: 'Line Total', type: 'money' },
                    { field: 'guestFirstName', header: 'First' },
                    { field: 'guestLastName',  header: 'Last' },
                ];
            case 'guestsOnProperty':
            case 'occupancyReservations':
                return [
                    { field: 'reservationId',    header: 'Res ID',     type: 'reservationLink', linkField: 'orderId' },
                    { field: 'propertyCode',     header: 'Property',   type: 'propertyCodeLink', linkField: 'shortTermRentalId' },
                    { field: 'guestFirstName',   header: 'First' },
                    { field: 'guestLastName',    header: 'Last' },
                    { field: 'bedrooms',         header: 'Bedrooms',   type: 'num' },
                    // KBM policy times — 4:00 PM check-in, 11:00 AM check-out.
                    // Stored values are date-only at midnight; rendering the
                    // raw time would show the browser's timezone-shifted
                    // midnight (typically 5:00 PM EDT or 2:00 PM HST), which
                    // is misleading. The policy formatters show the date as
                    // booked (UTC, no shift) plus the KBM check-in/out time.
                    { field: 'checkInDateTime',  header: 'Check-in',   type: 'checkInPolicy' },
                    { field: 'checkOutDateTime', header: 'Check-out',  type: 'checkOutPolicy' },
                    { field: 'earlyCheckInTag',  header: 'Early Check-in' },
                    { field: 'lateCheckoutTag',  header: 'Late Checkout' },
                    { field: 'reservationStatus',header: 'Status',     type: 'resStatus' },
                ];
            case 'futonsOut':
            case 'rollawaysOut':
                return [
                    { field: 'reservationId',    header: 'Res ID',   type: 'reservationLink', linkField: 'orderId' },
                    { field: 'propertyCode',     header: 'Property', type: 'propertyCodeLink', linkField: 'shortTermRentalId' },
                    { field: 'productName',      header: 'Product' },
                    { field: 'quantity',         header: 'Qty', type: 'num' },
                    { field: 'guestFirstName',   header: 'First' },
                    { field: 'guestLastName',    header: 'Last' },
                    { field: 'checkInDateTime',  header: 'Check-in',  type: 'checkInPolicy' },
                    { field: 'checkOutDateTime', header: 'Check-out', type: 'checkOutPolicy' },
                ];
            case 'cancellations':
                return [
                    { field: 'reservationId',    header: 'Res ID',   type: 'reservationLink', linkField: 'orderId' },
                    { field: 'propertyCode',     header: 'Property', type: 'propertyCodeLink', linkField: 'shortTermRentalId' },
                    { field: 'guestFirstName',   header: 'First' },
                    { field: 'guestLastName',    header: 'Last' },
                    // cancelDate is a real timestamp (when the cancellation
                    // happened); render with time. Was-Check-in is the date
                    // the stay would have started — use the policy formatter
                    // so it reads "M/D/YY, 4:00 PM" like every other drilldown.
                    { field: 'cancelDate',       header: 'Cancelled',     type: 'date' },
                    { field: 'checkInDateTime',  header: 'Was Check-in',  type: 'checkInPolicy' },
                    { field: 'revenueLost',      header: 'Revenue Lost',  type: 'money' },
                ];
            case 'bookingsCreated':
                return [
                    { field: 'orderId',          header: 'Order',    type: 'orderLink' },
                    // saleDate is a real timestamp — render with time. The
                    // "Channel" column was previously showing o.type ("ST" =
                    // Short Term, a listing type, not a channel). It now reads
                    // refSrcId via the channel formatter so VRBO / Airbnb /
                    // Booking.com / Website render as actual channel names.
                    { field: 'saleDate',         header: 'Booked',   type: 'date' },
                    { field: 'refSrcId',         header: 'Channel',  type: 'channel' },
                    { field: 'propertyCode',     header: 'Property', type: 'propertyCodeLink', linkField: 'shortTermRentalId' },
                    { field: 'guestFirstName',   header: 'First' },
                    { field: 'guestLastName',    header: 'Last' },
                    { field: 'checkInDateTime',  header: 'Check-in', type: 'checkInPolicy' },
                    { field: 'status',           header: 'Status',   type: 'resStatus' },
                ];
            case 'sameDayTurnovers':
                return [
                    { field: 'propertyCode',       header: 'Property',         type: 'propertyCodeLink', linkField: 'shortTermRentalId' },
                    { field: 'departingResId',     header: 'Departing Res',    type: 'reservationLink',  linkField: 'departingOrderId' },
                    { field: 'departingLastName',  header: 'Departing Guest' },
                    { field: 'arrivingResId',      header: 'Arriving Res',     type: 'reservationLink',  linkField: 'arrivingOrderId' },
                    { field: 'arrivingLastName',   header: 'Arriving Guest' },
                ];
            case 'priorityRooms':
                return [
                    { field: 'propertyCode',     header: 'Property', type: 'propertyCodeLink', linkField: 'shortTermRentalId' },
                    { field: 'reservationId',    header: 'Res ID',   type: 'reservationLink', linkField: 'orderId' },
                    { field: 'occupancyStatus',  header: 'Status',   type: 'roomStatus' },
                    { field: 'checkInDateTime',  header: 'Check-in', type: 'checkInPolicy' },
                ];
            default:
                return Object.keys(this.rows[0] || {}).map(k => ({ field: k, header: k }));
        }
    }

    format(row: any, col: { field: string; type?: string }): string {
        const v = row[col.field];
        if (v == null) return '—';
        switch (col.type) {
            case 'date':       return new Date(v).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' });
            case 'num':        return Number(v).toLocaleString('en-US');
            case 'money':      return Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
            case 'resStatus':       return this.RES_STATUS_NAMES[String(v)] || String(v);
            case 'roomStatus':      return this.ROOM_STATUS_NAMES[Number(v)] || String(v);
            // Same display formatting as 'roomStatus' — distinct type so the
            // HTML can wrap the cell in an <a> to the occupying reservation's
            // order page (linkField = 'occupyingOrderId').
            case 'roomStatusLink':  return this.ROOM_STATUS_NAMES[Number(v)] || String(v);
            case 'propertyLink': return String(v);
            case 'rating':     {
                // "4.80 / 5" — rating with scale denominator. ratingScale lives
                // on the same row; defaults to 5 (most KBM/VRBO reviews are
                // 5-point) when the scale field is null.
                const scale = row['ratingScale'] ?? 5;
                return `${Number(v).toFixed(2)} / ${scale}`;
            }
            // KBM policy: every check-in is at 4:00 PM. The underlying
            // checkInDateTime column is stored date-only at midnight, so
            // toLocaleString('en-US', {timeStyle:'short'}) renders the
            // timezone-shifted midnight (e.g. "5:00 PM" on EDT) which is
            // misleading. Format the calendar date with UTC interpretation
            // (no shift) and tack on the policy time.
            case 'checkInPolicy':  return this.formatDateUTC(v) + ', 4:00 PM';
            // KBM policy: every check-out is at 11:00 AM.
            case 'checkOutPolicy': return this.formatDateUTC(v) + ', 11:00 AM';
            // refSrcId → human channel name via the canonical refSrcImages
            // mapping (same source the reservation list + Channel Mix donut
            // use). NULL → "Direct". Unknown ID → "Source {id}" so the data
            // gap is visible rather than silently rendering empty.
            case 'channel': {
                if (v == null) return 'Direct';
                const num = Number(v);
                const match = this.constantService.refSrcImages.find(
                    (x: any) => x.value === num);
                return match ? match.label : `Source ${num}`;
            }
            default:           return String(v);
        }
    }

    /**
     * Render a date-only field as "M/D/YY" without applying the browser's
     * timezone offset. The underlying value is stored as midnight UTC on a
     * calendar day; using {@link Date#toLocaleDateString} with the default
     * (local) timezone shifts to the previous day for any negative-offset
     * zone (HST, PST, etc.). Forcing UTC keeps the displayed date equal to
     * what was actually booked.
     */
    private formatDateUTC(v: any): string {
        const d = new Date(v);
        return d.toLocaleDateString('en-US', {
            month: 'numeric', day: 'numeric', year: '2-digit', timeZone: 'UTC',
        });
    }
}
